import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

const ALL_CITIES_VALUE = "__all__";

function ComboSelect({ value, onChange, options, placeholder, disabled, testId, allowAllOption, allowAllLabel, creatable, emptyLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { t, lang } = useLang();
  const selected = value === ALL_CITIES_VALUE
    ? { name: allowAllLabel }
    : options.find((o) => o.name === value) || (value ? { name: value } : null);

  const label = (o) => (lang === "en" && o.name_en) ? o.name_en : o.name;

  const q = (query || "").trim().toLowerCase();
  const filtered = options.filter((o) => {
    if (!q) return true;
    return (o.name || "").toLowerCase().includes(q)
        || (o.name_en || "").toLowerCase().includes(q);
  });
  const canCreate = creatable && q && !options.some(
    (o) => o.name.trim() === query.trim() || (o.name_en || "").trim().toLowerCase() === q
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" disabled={disabled}
          data-testid={testId}
          className="w-full justify-between h-11 font-normal"
        >
          <span className={cn(!selected && "text-slate-400", "truncate text-start")}>
            {selected ? (typeof selected.name_en !== "undefined" ? label(selected) : selected.name) : placeholder}
          </span>
          <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t.filters.searchIn} value={query} onValueChange={setQuery} />
          <CommandList className="max-h-72">
            {filtered.length === 0 && !canCreate && (
              <CommandEmpty>{emptyLabel || t.common.noResults}</CommandEmpty>
            )}
            <CommandGroup>
              {allowAllOption && (
                <CommandItem value={ALL_CITIES_VALUE} onSelect={() => { onChange(""); setOpen(false); setQuery(""); }}>
                  <Check className={cn("me-2 h-4 w-4", (!value || value === ALL_CITIES_VALUE) ? "opacity-100" : "opacity-0")} />
                  <span className="font-semibold text-emerald-900">{allowAllLabel}</span>
                </CommandItem>
              )}
              {filtered.map((o) => (
                <CommandItem key={o.id || o.name} value={o.name}
                  onSelect={() => { onChange(o.name); setOpen(false); setQuery(""); }}>
                  <Check className={cn("me-2 h-4 w-4", value === o.name ? "opacity-100" : "opacity-0")} />
                  <span>{o.name}</span>
                  {o.name_en && <span className="ms-2 text-xs text-slate-400">{o.name_en}</span>}
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem value={`__create_${query}`}
                  onSelect={() => { onChange(query.trim()); setOpen(false); setQuery(""); }}>
                  <Plus className="me-2 h-4 w-4 text-emerald-700" />
                  <span className="text-emerald-800">{(lang === "ar" ? "استخدام: " : "Use: ") + query.trim()}</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * LocationPicker
 * Props: city, area, onChange({city, area}), allowAll=true, creatableArea=false, compact=false
 */
export default function LocationPicker({ city, area, onChange, allowAll = true, creatableArea = false, compact = false }) {
  const { t } = useLang();
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => { api.get("/locations/cities").then((r) => setCities(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!city) { setAreas([]); return; }
    api.get(`/locations/areas?city=${encodeURIComponent(city)}`).then((r) => setAreas(r.data.items || [])).catch(() => {});
  }, [city]);

  const grid = compact ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 sm:grid-cols-2 gap-3";

  return (
    <div className={grid}>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">{t.filters.city}</div>
        <ComboSelect
          testId="loc-city"
          value={city || ""} options={cities}
          placeholder={t.filters.allCities}
          allowAllOption={allowAll}
          allowAllLabel={t.filters.allCities}
          onChange={(v) => onChange({ city: v, area: "" })}
        />
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">{t.filters.area}</div>
        <ComboSelect
          testId="loc-area"
          value={area || ""} options={areas}
          placeholder={t.wizard.pickArea}
          disabled={!city}
          creatable={creatableArea}
          onChange={(v) => onChange({ city, area: v })}
        />
      </div>
    </div>
  );
}
